(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  // Require Algolia Shopify integration to be present
  if (typeof window.algoliaShopify === 'undefined' || !window.algoliaShopify.config) {
    console.warn('[AlgoliaRecommend] algoliaShopify config not found');
    return;
  }

  // Only fetch the attributes we actually need
  const ATTRIBUTES_TO_RETRIEVE = [
    'id',
    'objectID',
    'handle',
    'title',
    'image',
    'meta.custom.images',
    'price',
    'compare_at_price',
    'vendor',
    'product_type',
    'inventory_available',
    'meta.model.s3_folder_file',
    'meta.algolia.variant_promo_tag'
  ];

  class AlgoliaRecommendElement extends HTMLElement {
    constructor() {
      super();

      const ds = this.dataset || {};

      this.sectionId = ds.sectionId || '';
      this.mode = ds.mode || 'related-products';
      this.maxRelated = parseInt(ds.maxRelated, 10) || 8;
      this.maxFbt = parseInt(ds.maxFbt, 10) || 4;
      this.maxTrending = parseInt(ds.maxTrending, 10) || 8;

      this.headingRelated = ds.headingRelated || 'Related products';
      this.headingFbt = ds.headingFbt || 'Frequently bought together';
      this.headingTrending = ds.headingTrending || 'Trending items';

      // Per-model enable/disable toggles
      this.enableRelated = ds.enableRelated === 'true';
      this.enableFbt = ds.enableFbt === 'true';
      this.enableTrending = ds.enableTrending === 'true';

      // Per-model heading alignment: "left" | "center" | "right"
      this.headingAlignRelated = ds.headingAlignRelated || 'left';
      this.headingAlignFbt = ds.headingAlignFbt || 'left';
      this.headingAlignTrending = ds.headingAlignTrending || 'left';

      // Per-model heading size (h0–h6)
      this.headingSizeRelated = ds.headingSizeRelated || 'h3';
      this.headingSizeFbt = ds.headingSizeFbt || 'h3';
      this.headingSizeTrending = ds.headingSizeTrending || 'h3';

      // Per-model heading font family (e.g. "noe-text-semibold")
      this.headingFontRelated = ds.headingFontRelated || '';
      this.headingFontFbt = ds.headingFontFbt || '';
      this.headingFontTrending = ds.headingFontTrending || '';

      // Per-model layout: "grid" | "slider"
      this.layoutRelated = ds.layoutRelated || 'grid';
      this.layoutFbt = ds.layoutFbt || 'grid';
      this.layoutTrending = ds.layoutTrending || 'grid';

      // Per-model heading page-width wrapping
      this.headingPageWidthRelated = ds.headingPageWidthRelated === 'true';
      this.headingPageWidthFbt = ds.headingPageWidthFbt === 'true';
      this.headingPageWidthTrending = ds.headingPageWidthTrending === 'true';

      // Per-model layout page-width wrapping
      this.layoutPageWidthRelated = ds.layoutPageWidthRelated === 'true';
      this.layoutPageWidthFbt = ds.layoutPageWidthFbt === 'true';
      this.layoutPageWidthTrending = ds.layoutPageWidthTrending === 'true';

      // Slider behaviour flags (reuse your global slider toggles)
      this.swipeOnDesktop = ds.swipeOnDesktop === 'true';
      this.swipeOnTablet = ds.swipeOnTablet === 'true';
      this.swipeOnMobile = ds.swipeOnMobile === 'true';
      this.paginationType = ds.paginationType || 'arrows'; // "arrows" | "pagination" | "none"
      this.paginationLocation = ds.paginationLocation || 'bottom'; // "bottom" | "middle"

      // Anchor variant id coming from liquid (product.selected_or_first_available_variant.id)
      this.anchorObjectId = ds.anchorObjectId || '';

      this.palette = ds.palette || 'four';
      this.aspectRatio = ds.aspectRatio || 'square';
      this.showVendor = ds.showVendor === 'true';
      this.showPrice = ds.showPrice === 'true';
      this.showRating = ds.showRating === 'true';

      this.appId = window.algoliaShopify.config.app_id;
      this.apiKey = window.algoliaShopify.config.search_api_key;
      this.indexPrefix = window.algoliaShopify.config.index_prefix || '';
      this.productsIndexName = `${this.indexPrefix}products`;

      this.userToken =
        typeof window.getAlgoliaUserToken === 'function'
          ? window.getAlgoliaUserToken()
          : undefined;

      const template = window.theme && window.theme.template ? window.theme.template : 'unknown';
      if (template.indexOf('product') !== -1) {
        this.placementPrefix = 'PDP';
      } else if (template.indexOf('cart') !== -1) {
        this.placementPrefix = 'Cart';
      } else {
        this.placementPrefix = 'Other';
      }

      this.innerContainer =
        this.querySelector('.algolia-recommend__inner') || this;

      this.onRecommendClickBound = this.onRecommendClick.bind(this);
    }

    connectedCallback() {
      this.init();
    }

    async init() {
      try {
        // Always fetch the latest cart and use variant IDs in request-side filters
        const cartVariantIds = await this.fetchCartVariantIds();
        const { results, requestModels } = await this.fetchRecommendations(cartVariantIds);

        if (!results || !results.length) {
          return;
        }

        this.renderAllBlocks(results, requestModels);

        // Refresh Yotpo widgets if present (theme-specific hook)
        try {
          if (
            typeof window.yotpoWidgetsContainer !== 'undefined' &&
            typeof window.yotpoWidgetsContainer.initWidgets !== 'undefined'
          ) {
            window.yotpoWidgetsContainer.initWidgets();
          }
        } catch (e) {
          console.warn('[AlgoliaRecommend] Yotpo refresh failed', e);
        }
      } catch (e) {
        console.error('[AlgoliaRecommend] Error initializing recommendations', e);
      }
    }

    /**
     * Always fetch the current cart state, do not cache.
     * Returns an array of variant IDs as strings.
     */
    async fetchCartVariantIds() {
      try {
        const res = await fetch('/cart.js', {
          credentials: 'same-origin'
        });

        if (!res.ok) {
          throw new Error('Non-200 from /cart.js');
        }

        const cart = await res.json();
        const ids = (cart.items || []).map((item) => String(item.variant_id));
        return ids;
      } catch (e) {
        console.warn('[AlgoliaRecommend] Could not read /cart.js; cart variant exclusion disabled', e);
        return [];
      }
    }

    /**
     * Build a base Algolia filters string.
     * - Only in-stock products
     * - Only items with meta.algolia.ignore == "public"
     * - Exclude part-diagram products (product_type "Part Diagram")
     * - Exclude any variant IDs passed in (these are cart variants)
     *   by filtering on objectID:<variant_id> (variant-level objectID).
     */
    buildBaseFilters(cartVariantIds, excludeAnchorToo) {
      // Base rules
      let filters = 'inventory_available:true AND meta.algolia.ignore:public AND NOT product_type:"Part Diagram"';

      const idsToExclude = [];

      // Optionally exclude the anchor variant from recommendations as well
      if (excludeAnchorToo && this.anchorObjectId) {
        idsToExclude.push(String(this.anchorObjectId));
      }

      if (Array.isArray(cartVariantIds)) {
        cartVariantIds.forEach((id) => idsToExclude.push(String(id)));
      }

      if (idsToExclude.length) {
        const notFilters = idsToExclude.map((id) => `NOT objectID:${id}`).join(' AND ');
        filters += ` AND ${notFilters}`;
      }

      return filters;
    }

    /**
     * Build Recommend API requests using request-side filters,
     * including cart variant exclusion and per-model toggles.
     */
    buildRequests(cartVariantIds) {
      const requests = [];

      const baseQueryParameters = {
        attributesToRetrieve: ATTRIBUTES_TO_RETRIEVE,
        // Request-side filters:
        // - in stock
        // - ignore == public
        // - NOT Part Diagram
        // - NOT any variant currently in cart (via objectID)
        filters: this.buildBaseFilters(cartVariantIds, true),
        clickAnalytics: true
      };

      if (this.userToken) {
        baseQueryParameters.userToken = this.userToken;
      }

      const shouldLoadRelated =
        this.enableRelated &&
        (this.mode === 'related-products' || this.mode === 'all');
      const shouldLoadFbt =
        this.enableFbt &&
        (this.mode === 'frequently-bought-together' || this.mode === 'all');
      const shouldLoadTrending =
        this.enableTrending &&
        (this.mode === 'trending-items' || this.mode === 'all');

      // Related products & FBT need an anchor variant
      if (shouldLoadRelated && this.anchorObjectId) {
        requests.push({
          indexName: this.productsIndexName,
          model: 'related-products',
          objectID: String(this.anchorObjectId),
          maxRecommendations: this.maxRelated,
          threshold: 0,
          queryParameters: baseQueryParameters
        });
      }

      if (shouldLoadFbt && this.anchorObjectId) {
        // NOTE: Algolia model is actually "bought-together"
        requests.push({
          indexName: this.productsIndexName,
          model: 'bought-together',
          objectID: String(this.anchorObjectId),
          maxRecommendations: this.maxFbt,
          threshold: 0,
          queryParameters: baseQueryParameters
        });
      }

      if (shouldLoadTrending) {
        requests.push({
          indexName: this.productsIndexName,
          model: 'trending-items',
          maxRecommendations: this.maxTrending,
          threshold: 0,
          queryParameters: baseQueryParameters
        });
      }

      return requests;
    }

    /**
     * Call the Recommend REST endpoint with our requests.
     * No client-side hit filtering for cart or part diagrams — all done via filters.
     */
    async fetchRecommendations(cartVariantIds) {
      const requests = this.buildRequests(cartVariantIds);

      if (!requests.length) {
        return { results: [], requestModels: [] };
      }

      const endpoint =
        `https://${this.appId}-dsn.algolia.net/1/indexes/*/recommendations` +
        `?x-algolia-agent=${encodeURIComponent(
          'Algolia for JavaScript (4.23.3); Recommend (4.23.3); Browser; Shopify Integration; Custom Recommend Block'
        )}` +
        `&x-algolia-application-id=${this.appId}` +
        `&x-algolia-api-key=${this.apiKey}`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({ requests })
      });

      if (!res.ok) {
        throw new Error(`Recommend request failed with status ${res.status}`);
      }

      const json = await res.json();
      const results = json && Array.isArray(json.results) ? json.results : [];

      return {
        results,
        requestModels: requests.map((r) => r.model || 'trending-items')
      };
    }

    renderAllBlocks(results, requestModels) {
      this.innerContainer.innerHTML = '';

      results.forEach((result, idx) => {
        const model = requestModels[idx];
        const hits = result.hits || [];

        if (!hits.length) {
          return;
        }

        const layout = this.getLayoutForModel(model);

        if (model === 'related-products') {
          this.renderBlock(model, hits, this.headingRelated, layout);
        } else if (model === 'bought-together' || model === 'frequently-bought-together') {
          this.renderBlock(model, hits, this.headingFbt, layout);
        } else if (model === 'trending-items') {
          this.renderBlock(model, hits, this.headingTrending, layout);
        }
      });
    }

    /**
     * Map a model to its layout: "grid" | "slider"
     */
    getLayoutForModel(model) {
      let layout = 'grid';

      if (model === 'related-products') {
        layout = this.layoutRelated;
      } else if (model === 'bought-together' || model === 'frequently-bought-together') {
        layout = this.layoutFbt;
      } else if (model === 'trending-items') {
        layout = this.layoutTrending;
      }

      if (layout !== 'slider') {
        layout = 'grid';
      }

      return layout;
    }

    /**
     * Map a model to its heading alignment setting.
     */
    getHeadingAlignmentClass(model) {
      let align = 'left';

      if (model === 'related-products') {
        align = this.headingAlignRelated;
      } else if (model === 'bought-together' || model === 'frequently-bought-together') {
        align = this.headingAlignFbt;
      } else if (model === 'trending-items') {
        align = this.headingAlignTrending;
      }

      if (align !== 'left' && align !== 'center' && align !== 'right') {
        align = 'left';
      }

      return `algolia-recommend__heading--${align}`;
    }

    /**
     * Per-model heading size (h0–h6)
     */
    getHeadingSizeClass(model) {
      let size = 'h3';

      if (model === 'related-products') {
        size = this.headingSizeRelated;
      } else if (model === 'bought-together' || model === 'frequently-bought-together') {
        size = this.headingSizeFbt;
      } else if (model === 'trending-items') {
        size = this.headingSizeTrending;
      }

      const allowed = ['h0', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
      if (!allowed.includes(size)) {
        size = 'h3';
      }

      return size;
    }

    /**
     * Per-model heading font (adds font--XXXX class)
     */
    getHeadingFontClass(model) {
      let font = '';

      if (model === 'related-products') {
        font = this.headingFontRelated;
      } else if (model === 'bought-together' || model === 'frequently-bought-together') {
        font = this.headingFontFbt;
      } else if (model === 'trending-items') {
        font = this.headingFontTrending;
      }

      if (!font) return '';

      return `font--${font}`;
    }

    /**
     * Whether heading should be wrapped in .page-width
     */
    getHeadingPageWidth(model) {
      if (model === 'related-products') {
        return this.headingPageWidthRelated;
      }
      if (model === 'bought-together' || model === 'frequently-bought-together') {
        return this.headingPageWidthFbt;
      }
      if (model === 'trending-items') {
        return this.headingPageWidthTrending;
      }
      return false;
    }

    /**
     * Whether layout (grid/slider) should be wrapped in .page-width
     */
    getLayoutPageWidth(model) {
      if (model === 'related-products') {
        return this.layoutPageWidthRelated;
      }
      if (model === 'bought-together' || model === 'frequently-bought-together') {
        return this.layoutPageWidthFbt;
      }
      if (model === 'trending-items') {
        return this.layoutPageWidthTrending;
      }
      return false;
    }

    renderBlock(model, hits, headingText, layout) {
      if (!hits || !hits.length) return;

      const isSlider = layout === 'slider';

      const block = document.createElement('section');
      block.className = `algolia-recommend__block algolia-recommend__block--${model}`;

      // Heading
      if (headingText) {
        const heading = document.createElement('h2');

        const classes = [
          'algolia-recommend__heading',
          this.getHeadingSizeClass(model),
          this.getHeadingAlignmentClass(model)
        ];

        const fontClass = this.getHeadingFontClass(model);
        if (fontClass) {
          classes.splice(1, 0, fontClass);
        }

        heading.className = classes.join(' ');
        heading.textContent = headingText;

        if (this.getHeadingPageWidth(model)) {
          const headingWrapper = document.createElement('div');
          headingWrapper.className = 'page-width';
          headingWrapper.appendChild(heading);
          block.appendChild(headingWrapper);
        } else {
          block.appendChild(heading);
        }
      }

      // Layout (grid or slider)
      let layoutRoot = null;

      if (isSlider) {
        const slider = document.createElement('slider-component');
        slider.className = 'slider-mobile-gutter slider-component-desktop';

        const ul = this.buildProductsListElement(model, headingText, true);

        hits.forEach((hit, index) => {
          this.renderProductCard(hit, ul, model, index, true);
        });

        slider.appendChild(ul);

        if (
          this.paginationType !== 'none' &&
          (this.swipeOnDesktop || this.swipeOnTablet || this.swipeOnMobile)
        ) {
          const controls = this.buildSliderControls(ul.id, hits.length);
          if (controls) {
            slider.appendChild(controls);
          }
        }

        layoutRoot = slider;
      } else {
        const ul = this.buildProductsListElement(model, headingText, false);

        hits.forEach((hit, index) => {
          this.renderProductCard(hit, ul, model, index, false);
        });

        layoutRoot = ul;
      }

      if (layoutRoot) {
        if (this.getLayoutPageWidth(model)) {
          const wrapper = document.createElement('div');
          wrapper.className = 'page-width';
          wrapper.appendChild(layoutRoot);
          block.appendChild(wrapper);
        } else {
          block.appendChild(layoutRoot);
        }
      }

      this.innerContainer.appendChild(block);

      // Fire "viewedObjectIDs" event per block
      try {
        if (typeof window.aa === 'function' && hits.length) {
          window.aa('viewedObjectIDs', {
            userToken: this.userToken || 'anonymous',
            index: this.productsIndexName,
            eventName: `${this.placementPrefix}: ${this.getModelLabel(model)} Viewed`,
            objectIDs: hits.map((h) => String(h.objectID))
          });
        }
      } catch (e) {
        console.warn('[AlgoliaRecommend] viewedObjectIDs failed', e);
      }
    }

    /**
     * Build the <ul> element used for products, shared between grid & slider.
     * IMPORTANT: use fixed grid classes so grid + slider share the same base widths.
     * - 2-col tablet-down
     * - 4-col desktop
     */
    buildProductsListElement(model, headingText, isSlider) {
      const ul = document.createElement('ul');

      const baseId = `AlgoliaRecommend-${this.sectionId || 'section'}-${model}`;
      ul.id = isSlider ? `${baseId}-Slider` : `${baseId}-Grid`;

      const classes = [
        'grid',
        'product-grid',
        'algolia-recommend__grid',
        'grid--2-col-tablet-down',
        'grid--4-col-desktop'
      ];

      if (isSlider && (this.swipeOnDesktop || this.swipeOnTablet || this.swipeOnMobile)) {
        classes.push('slider');
        if (this.swipeOnDesktop) classes.push('slider--desktop');
        if (this.swipeOnTablet) {
          classes.push('slider--tablet', 'grid--peek');
        }
        if (this.swipeOnMobile) {
          classes.push('slider--mobile', 'grid--peek');
        }
      }

      ul.className = classes.join(' ');
      ul.setAttribute('role', 'list');

      const fallbackLabel = this.getModelLabel(model);
      ul.setAttribute('aria-label', headingText || fallbackLabel);

      return ul;
    }

    /**
     * Build slider controls (prev / counter / next), Dawn-style.
     */
    buildSliderControls(listId, totalSlides) {
      if (this.paginationType === 'none') return null;

      const wrapper = document.createElement('div');
      wrapper.className = [
        'hidetablet',
        'slider-buttons',
        'no-js-hidden',
        this.paginationType
      ].join(' ');
      if (this.paginationLocation === 'middle') {
        wrapper.className += ' slider-button--middle';
      }

      const prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'slider-button slider-button--prev';
      prev.name = 'previous';
      prev.setAttribute(
        'aria-label',
        (window.theme &&
          window.theme.strings &&
          window.theme.strings.previousSlide) ||
          'Previous slide'
      );
      prev.setAttribute('aria-controls', listId);
      prev.innerHTML = this.getSliderCaretSvg();

      const counter = document.createElement('div');
      counter.className = 'slider-counter caption';
      counter.innerHTML = `
        <span class="slider-counter--current">1</span>
        <span aria-hidden="true"> / </span>
        <span class="visually-hidden">${
          (window.theme &&
            window.theme.strings &&
            window.theme.strings.of) ||
          'of'
        }</span>
        <span class="slider-counter--total">${totalSlides}</span>
      `;

      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'slider-button slider-button--next';
      next.name = 'next';
      next.setAttribute(
        'aria-label',
        (window.theme &&
          window.theme.strings &&
          window.theme.strings.nextSlide) ||
          'Next slide'
      );
      next.setAttribute('aria-controls', listId);
      next.innerHTML = this.getSliderCaretSvg();

      wrapper.appendChild(prev);
      wrapper.appendChild(counter);
      wrapper.appendChild(next);

      return wrapper;
    }

    /**
     * Inline caret icon SVG (matches existing caret path).
     */
    getSliderCaretSvg() {
      return `
        <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
        </svg>
      `;
    }

    renderProductCard(hit, listEl, model, position, isSlider) {
      const li = document.createElement('li');
      li.className = 'grid__item';
      if (isSlider) {
        li.classList.add('slider__slide');
      }

      const card = document.createElement('div');
      card.className = `card standard-card palette--${this.palette}`;

      const productUrl = `/products/${hit.handle}?variant=${encodeURIComponent(
        hit.objectID
      )}`;

      // Variant promo tag from Algolia metafield (only if in stock)
      const promoTag = this.getPromoTagFromHit(hit);
      if (promoTag && hit.inventory_available) {
        const tag = document.createElement('div');
        tag.className = 'card__tag card__tag--promo';
        tag.textContent = promoTag;
        card.appendChild(tag);
      }

      // Image
      const media = document.createElement('div');
      media.className = 'card__media';

      const link = document.createElement('a');
      link.className = 'full-unstyled-link';
      link.href = productUrl;
      link.dataset.objectId = String(hit.objectID);
      link.dataset.recommendModel = model;
      link.dataset.indexName = this.productsIndexName;
      link.dataset.position = String(position);
      link.addEventListener('click', this.onRecommendClickBound);

      const mediaInner = document.createElement('div');
      mediaInner.className = `media media--contains media--${this.aspectRatio}`;

      let images = [];
      if (hit.meta && hit.meta.custom && hit.meta.custom.images) {
        images = String(hit.meta.custom.images).split(',');
      } else if (hit.image) {
        images = [hit.image];
      }

      let src = images[0];
      if (!src && window.theme && window.theme.productDefaultImage) {
        src = window.theme.productDefaultImage;
      }

      if (src) {
        const img = document.createElement('img');
        img.className = 'motion-reduce';
        img.loading = 'lazy';
        img.width = 1080;
        img.height = 1080;
        img.src = `${src}&width=1080`;
        img.srcset = [
          `${src}&width=180 180w`,
          `${src}&width=360 360w`,
          `${src}&width=540 540w`,
          `${src}&width=720 720w`,
          `${src}&width=900 900w`,
          `${src}&width=1080 1080w`
        ].join(', ');
        img.sizes =
          '(min-width: 990px) calc(100vw / 4), (min-width: 750px) calc(100vw / 3), calc(100vw / 2)';
        img.alt = hit.title || '';

        mediaInner.appendChild(img);
      }

      link.appendChild(mediaInner);
      media.appendChild(link);
      card.appendChild(media);

      // AR button if 3D model exists
      if (hit.meta && hit.meta.model && hit.meta.model.s3_folder_file) {
        const arButton = document.createElement('ar-button');
        arButton.className = 'ar-button';
        arButton.dataset.handle = hit.handle;
        arButton.innerHTML = `
          <button 
            aria-haspopup="dialog"
            aria-expanded="false"
            aria-controls="ARViewerPopupModalContent"
            class="ar-button-link full-unstyled-link"
          >
            ${
              window.theme &&
              window.theme.strings &&
              window.theme.strings.ar
                ? window.theme.strings.ar
                : 'View in 3D'
            }
          </button>
        `;
        media.appendChild(arButton);
      }

      const inner = document.createElement('div');
      inner.className = 'card__inner';

      const info = document.createElement('div');
      info.className = 'card__information center';

      if (this.showVendor && hit.vendor) {
        const vendor = document.createElement('div');
        vendor.className = 'card__super';
        vendor.innerHTML = `
          <span class="visually-hidden">${
            window.accessibilityStrings && window.accessibilityStrings.vendor
              ? window.accessibilityStrings.vendor
              : 'Vendor'
          }</span>
          <span>${hit.vendor}</span>
        `;
        info.appendChild(vendor);
      }

      const heading = document.createElement('h2');
      heading.className = 'card__heading h4';

      const titleLink = document.createElement('a');
      titleLink.className = 'full-unstyled-link';
      titleLink.href = productUrl;
      titleLink.textContent = hit.title || '';

      heading.appendChild(titleLink);
      info.appendChild(heading);

      if (this.showPrice && typeof hit.price !== 'undefined') {
        const priceWrapper = document.createElement('div');
        priceWrapper.className = 'card__price';

        const priceValue =
          typeof hit.price === 'number' ? hit.price : parseFloat(hit.price);
        const compareAtRaw =
          typeof hit.compare_at_price === 'number'
            ? hit.compare_at_price
            : parseFloat(hit.compare_at_price);
        const hasCompareAt =
          !isNaN(compareAtRaw) &&
          !isNaN(priceValue) &&
          compareAtRaw > priceValue;

        const money = (cents) => {
          if (
            window.algoliaShopify &&
            typeof window.algoliaShopify.formatMoney === 'function'
          ) {
            return window.algoliaShopify.formatMoney(cents);
          }
          return `$${(cents / 100).toFixed(2)}`;
        };

        const container = document.createElement('div');
        container.className =
          'card__price ' +
          (hit.inventory_available ? '' : 'price--sold-out') +
          (hasCompareAt ? ' price--on-sale' : ' price--no-compare');

        container.innerHTML = `
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">${
                window.theme &&
                window.theme.strings &&
                window.theme.strings.regularPrice
                  ? window.theme.strings.regularPrice
                  : 'Regular price'
              }</span>
              <span class="price-item price-item--regular">
                ${money(priceValue * 100)}
              </span>
            </div>
            ${
              hasCompareAt
                ? `
                  <div class="price__sale">
                    <div class="price__container">
                      <span class="visually-hidden visually-hidden--inline">${
                        window.theme &&
                        window.theme.strings &&
                        window.theme.strings.salePrice
                          ? window.theme.strings.salePrice
                          : 'Sale price'
                      }</span>
                      <span class="price-item price-item--sale price-item--last">
                        ${money(priceValue * 100)}
                      </span>
                      <span class="visually-hidden visually-hidden--inline">${
                        window.theme &&
                        window.theme.strings &&
                        window.theme.strings.regularPrice
                          ? window.theme.strings.regularPrice
                          : 'Regular price'
                      }</span>
                      <span>
                        <s class="price-item price-item--regular">
                          ${money(compareAtRaw * 100)}
                        </s>
                      </span>
                    </div>
                  </div>
                `
                : ''
            }
          </div>
        `;

        priceWrapper.appendChild(container);
        info.appendChild(priceWrapper);
      }

      if (
        this.showRating &&
        hit.product_type !== 'Part Diagram' &&
        window.theme &&
        window.theme.yotpo &&
        window.theme.yotpo.bottomlineInstanceId
      ) {
        const rating = document.createElement('div');
        rating.className = 'card__rating';
        rating.innerHTML = `
          <a class="yotpo-prod-link" href="${productUrl}">
            <div 
              class="yotpo-widget-instance"
              data-yotpo-instance-id="${window.theme.yotpo.bottomlineInstanceId}"
              data-yotpo-product-id="${hit.id}"
              data-yotpo-cart-product-id="${hit.id}"
              data-yotpo-section-id="recommend"></div>
          </a>
        `;
        info.appendChild(rating);
      }

      inner.appendChild(info);
      card.appendChild(inner);
      li.appendChild(card);
      listEl.appendChild(li);
    }

    onRecommendClick(event) {
      try {
        const link = event.currentTarget;
        const objectId = link.dataset.objectId;
        const model = link.dataset.recommendModel || 'unknown';
        const indexName = link.dataset.indexName || this.productsIndexName;
        const position = parseInt(link.dataset.position || '0', 10) + 1;

        if (typeof window.aa === 'function' && objectId) {
          window.aa('clickedObjectIDs', {
            userToken: this.userToken || 'anonymous',
            index: indexName,
            eventName: `${this.placementPrefix}: ${this.getModelLabel(model)} Clicked`,
            objectIDs: [String(objectId)],
            positions: [position]
          });
        }
      } catch (e) {
        console.warn('[AlgoliaRecommend] clickedObjectIDs failed', e);
      }
    }

    getModelLabel(model) {
      if (model === 'related-products') return 'Related Products';
      if (model === 'bought-together' || model === 'frequently-bought-together') {
        return 'Frequently Bought Together';
      }
      if (model === 'trending-items') return 'Trending Items';
      return 'Recommend';
    }

    /**
     * Read variant promo tag from Algolia hit.
     * Source: hit.meta.algolia.variant_promo_tag
     *
     * @param {object} hit
     * @returns {string|null}
     */
    getPromoTagFromHit(hit) {
      const value =
        hit &&
        hit.meta &&
        hit.meta.algolia &&
        hit.meta.algolia.variant_promo_tag;

      if (typeof value !== 'string') return null;

      const trimmed = value.trim();
      return trimmed.length ? trimmed : null;
    }
  }

  if (!customElements.get('algolia-recommend')) {
    customElements.define('algolia-recommend', AlgoliaRecommendElement);
  }
})();
